/**
 * Single source of truth for Paddle product pricing + refund policy.
 *
 * Refund policy (Option D: Hybrid):
 *   - Within 14 days:
 *       • 0 audits used        → 100% refund
 *       • 1–30 used (≤75%)     → prorated refund of unused
 *       • 31+ used (>75% cap)  → NO refund (abuse prevention)
 *   - After 14 days: no refund (except service failure, manual review)
 *
 *   Enforcement lives in Paddle dashboard rules + manual review — not in code.
 *   This file provides the math for displaying value consumed/remaining to
 *   the user and for policy documentation.
 */

export const RETAINER_PRICE_CENTS = 19900 // $199.00
export const RETAINER_AUDITS = 40
export const RETAINER_REFUND_WINDOW_DAYS = 14
export const RETAINER_ABUSE_CAP_RATIO = 0.75 // >75% consumed = no refund

export const PACK_PRICING = {
  'pack-single': { priceCents: 500, audits: 1 }, // $5 × 1 = $5/audit
  'pack-starter': { priceCents: 1200, audits: 3 }, // $12 / 3 = $4/audit
  'pack-power': { priceCents: 4000, audits: 10 }, // $40 / 10 = $4/audit
} as const

export const RETAINER_PER_AUDIT_CENTS = Math.round(
  RETAINER_PRICE_CENTS / RETAINER_AUDITS
) // $4.975 → display as $4.97 or $4.98

/**
 * Format cents as a USD string (e.g. 19900 → "$199.00").
 */
export function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

/**
 * Given a retainer's monthly_quota and usage_this_month, compute:
 *   - dollar value consumed so far
 *   - dollar value remaining
 *   - whether the user is over the abuse cap (no refund eligible)
 */
export function retainerValueBreakdown(params: {
  monthlyQuota: number
  usageThisMonth: number
}): {
  consumedCents: number
  remainingCents: number
  consumedLabel: string
  remainingLabel: string
  overAbuseCap: boolean
} {
  const { monthlyQuota, usageThisMonth } = params
  const perAudit = RETAINER_PRICE_CENTS / monthlyQuota
  const consumedCents = Math.round(perAudit * usageThisMonth)
  const remainingCents = RETAINER_PRICE_CENTS - consumedCents
  const overAbuseCap = usageThisMonth / monthlyQuota > RETAINER_ABUSE_CAP_RATIO
  return {
    consumedCents,
    remainingCents,
    consumedLabel: formatDollars(consumedCents),
    remainingLabel: formatDollars(remainingCents),
    overAbuseCap,
  }
}

/**
 * Is the retainer still within the 14-day refund window?
 * period_start is the date of the current billing cycle.
 */
export function isWithinRefundWindow(periodStart: Date | string): boolean {
  const start =
    typeof periodStart === 'string' ? new Date(periodStart) : periodStart
  const ageDays = (Date.now() - start.getTime()) / (1000 * 60 * 60 * 24)
  return ageDays <= RETAINER_REFUND_WINDOW_DAYS
}
